'use strict'

/**
 * マトリクス本体テーブル（横スクロール領域・2D 仮想スクロール・ヘッダ・行フィルタ・tbody）。 派生データ（filteredColumns / leftColumnRowsFiltered / statusByCode /
 * rowFilterOptions）と ハンドラを props で受け取る。
 *
 * 2D 仮想スクロール（ウィンドウィング）: 画面に見えている列・行＋前後の overscan だけを描画し、 画面外は左右の spacer セル・上下の spacer
 * 行で幅／高さだけ確保する。これにより、列数・行数が数百〜数千あっても 1 画面で描画するセル数は一定に保たれ、初期表示・縦横スクロールが軽い。
 *
 * 結合セル（大分類/中分類/ダッシュボード）は computeWindowedRowSpans で行ウィンドウに合わせて span を丸め、 spacer
 * 行へはみ出さないようにする（ウィンドウ先頭がグループ途中でも先頭行にラベルを描画）。 スクロール位置は STEP 単位に量子化し、overscan で空白を隠すことで再レンダー頻度を抑える。
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import CellDot from './CellDot'
import MatrixRow from './MatrixRow'
import RowFilterDropdown from './RowFilterDropdown'
import {
  cascadeRowFilters,
  getRowFilterCountLabel,
  groupByDataClassification,
  computeWindowedRowSpans
} from '../extra/helpers'
import {
  COLUMN_GROUP_BORDER_COLOR,
  COLUMN_OVERSCAN,
  COLUMN_WINDOW_STEP,
  COLUMN_VIEWPORT_FALLBACK,
  ROW_OVERSCAN,
  ROW_WINDOW_STEP,
  ROW_HEIGHT_FALLBACK,
  ROW_VIEWPORT_FALLBACK,
  LEFT_COLUMNS_TOTAL_WIDTH,
  COLUMN_WIDTH_WIDE,
  COLUMN_WIDTH_NARROW,
  FEW_COLUMNS_THRESHOLD,
  COL_WIDTH_MAJOR,
  COL_WIDTH_MIDDLE,
  COL_WIDTH_DASHBOARD,
  COL_WIDTH_GRAPH,
  COL_WIDTH_REFERABLE
} from '../extra/constants'
import { ACTION_TYPES, useDashboardMatrixDispatch, useDashboardMatrixSelector } from '../extra/store'

/** 大分類のグループキー */
const majorKey = (r) => r.majorLabel
/** 中分類のグループキー */
const middleKey = (r) => r.middleName
/** ダッシュボードのグループキー */
const dashboardKey = (r) => `${r.majorId}|${r.middleName}|${r.dashboardName}`

/**
 * @param {{
 *   filteredColumns: object[]
 *   leftColumnRowsFiltered: object[]
 *   statusByCode: { [code: string]: 'set' | 'unset' | 'remove' }
 *   rowFilterOptions: { major: string[]; middle: string[]; dashboard: string[]; graph: string[] }
 *   loading: boolean
 *   error: string | null
 *   mappingLoading: boolean
 *   overlayLoading: boolean
 *   onFilterChange: (payload: object) => void
 *   onGraphClick: (row: object) => void
 *   onColumnInfoClick: (col: object) => void
 * }} props
 * @returns {JSX.Element}
 */
function MatrixTable({
  filteredColumns,
  leftColumnRowsFiltered,
  statusByCode,
  rowFilterOptions,
  loading,
  error,
  mappingLoading,
  overlayLoading,
  onFilterChange,
  onGraphClick,
  onColumnInfoClick
}) {
  const dispatch = useDashboardMatrixDispatch()
  const rowFilters = useDashboardMatrixSelector((s) => s.filters.rowFilters)

  // 行フィルタ UI（ヘッダのドロップダウン）
  const [openRowFilter, setOpenRowFilter] = useState(null) // 'major' | 'middle' | 'dashboard' | 'graph' | 'referable'
  const [rowFilterDropdownRect, setRowFilterDropdownRect] = useState(null)
  const rowFilterAnchorRefs = useRef({ major: null, middle: null, dashboard: null, graph: null, referable: null })

  // ヘッダ高さ・2D 仮想スクロール
  const [headerRow1Height, setHeaderRow1Height] = useState(0)
  const [theadHeight, setTheadHeight] = useState(0)
  const [columnsReady, setColumnsReady] = useState(false)
  const [colWindowStart, setColWindowStart] = useState(0) // 表示列ウィンドウ先頭（STEP 量子化）
  const [rowWindowStart, setRowWindowStart] = useState(0) // 表示行ウィンドウ先頭（STEP 量子化）
  const [viewportWidth, setViewportWidth] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)
  const [rowHeight, setRowHeight] = useState(ROW_HEIGHT_FALLBACK)
  const headerRow1Ref = useRef(null)
  const theadRef = useRef(null)
  const tableScrollRef = useRef(null)
  const prevLoadingRef = useRef(loading)

  // --- 列ウィンドウ ---
  const totalCols = filteredColumns.length
  const fewColumns = totalCols <= FEW_COLUMNS_THRESHOLD
  const colWidth = fewColumns ? COLUMN_WIDTH_WIDE : COLUMN_WIDTH_NARROW
  const effViewportW = viewportWidth || COLUMN_VIEWPORT_FALLBACK
  const visibleCols = Math.ceil(effViewportW / colWidth)
  const clampedColStart = Math.min(colWindowStart, Math.max(0, totalCols - 1))
  const colStart = Math.max(0, clampedColStart - COLUMN_OVERSCAN)
  const colEnd = Math.min(totalCols, clampedColStart + visibleCols + COLUMN_OVERSCAN)
  const windowColumns = useMemo(() => filteredColumns.slice(colStart, colEnd), [filteredColumns, colStart, colEnd])
  const groupedWindowColumns = useMemo(() => groupByDataClassification(windowColumns), [windowColumns])
  const leftColSpacerWidth = colStart * colWidth
  const rightColSpacerWidth = (totalCols - colEnd) * colWidth

  // --- 行ウィンドウ ---
  const totalRows = leftColumnRowsFiltered.length
  const effViewportH = viewportHeight || ROW_VIEWPORT_FALLBACK
  const visibleRows = Math.ceil(effViewportH / rowHeight)
  const clampedRowStart = Math.min(rowWindowStart, Math.max(0, totalRows - 1))
  const rowStart = Math.max(0, clampedRowStart - ROW_OVERSCAN)
  const rowEnd = Math.min(totalRows, clampedRowStart + visibleRows + ROW_OVERSCAN)
  const windowRows = useMemo(
    () => leftColumnRowsFiltered.slice(rowStart, rowEnd),
    [leftColumnRowsFiltered, rowStart, rowEnd]
  )
  const windowedSpans = useMemo(
    () => ({
      major: computeWindowedRowSpans(leftColumnRowsFiltered, majorKey, rowStart, rowEnd),
      middle: computeWindowedRowSpans(leftColumnRowsFiltered, middleKey, rowStart, rowEnd),
      dashboard: computeWindowedRowSpans(leftColumnRowsFiltered, dashboardKey, rowStart, rowEnd)
    }),
    [leftColumnRowsFiltered, rowStart, rowEnd]
  )
  const topSpacerHeight = rowStart * rowHeight
  const bottomSpacerHeight = (totalRows - rowEnd) * rowHeight

  // メッセージ行・spacer 行の colSpan: 左 5 + 列 spacer 2 + ウィンドウ列。
  const totalColSpan = 5 + 2 + windowColumns.length

  // --- 行フィルタの初期化（すべて選択）と連動（cascade） ---
  const rowFilterInitializedRef = useRef(false)
  useEffect(() => {
    if (rowFilterInitializedRef.current) return
    const hasOptions =
      rowFilterOptions.major.length > 0 ||
      rowFilterOptions.middle.length > 0 ||
      rowFilterOptions.dashboard.length > 0 ||
      rowFilterOptions.graph.length > 0
    if (!hasOptions) return
    rowFilterInitializedRef.current = true
    dispatch(ACTION_TYPES.SET_FILTERS, {
      rowFilters: {
        major: [...rowFilterOptions.major],
        middle: [...rowFilterOptions.middle],
        dashboard: [...rowFilterOptions.dashboard],
        graph: [...rowFilterOptions.graph]
      }
    })
  }, [rowFilterOptions, dispatch])

  const prevOptionsRef = useRef(rowFilterOptions)
  useEffect(() => {
    if (!rowFilterInitializedRef.current) return
    const rf = rowFilters
    if (rf.major.length === 0 && rf.middle.length === 0 && rf.dashboard.length === 0 && rf.graph.length === 0) return
    const opt = rowFilterOptions
    const prevOpt = prevOptionsRef.current
    prevOptionsRef.current = opt

    const cascaded = cascadeRowFilters(rf, opt, prevOpt)
    if (
      cascaded.middle.length !== rf.middle.length ||
      cascaded.dashboard.length !== rf.dashboard.length ||
      cascaded.graph.length !== rf.graph.length
    ) {
      dispatch(ACTION_TYPES.SET_FILTERS, { rowFilters: { ...rf, ...cascaded } })
    }
  }, [rowFilterOptions, rowFilters, dispatch])

  // --- 行フィルタドロップダウンの位置と外側クリック・スクロール追従 ---
  useEffect(() => {
    if (!openRowFilter) return
    const onDocClick = (e) => {
      if (!e.target.closest(`[data-row-filter="${openRowFilter}"]`)) setOpenRowFilter(null)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [openRowFilter])

  const updateRowFilterDropdownRect = useCallback(() => {
    if (!openRowFilter) return
    const el = rowFilterAnchorRefs.current[openRowFilter]
    if (!el) return
    const rect = el.getBoundingClientRect()
    setRowFilterDropdownRect({ left: rect.left, top: rect.bottom + 2, width: rect.width })
  }, [openRowFilter])

  useLayoutEffect(() => {
    if (!openRowFilter) {
      setRowFilterDropdownRect(null)
      return
    }
    updateRowFilterDropdownRect()
  }, [openRowFilter, updateRowFilterDropdownRect])

  useEffect(() => {
    if (!openRowFilter) return
    const onScroll = () => updateRowFilterDropdownRect()
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [openRowFilter, updateRowFilterDropdownRect])

  // --- ヘッダ1行目の高さ計測（2行目 sticky の top に使う） ---
  useEffect(() => {
    const el = headerRow1Ref.current
    if (!el) return
    const observer = new ResizeObserver(() => setHeaderRow1Height(el.getBoundingClientRect().height))
    observer.observe(el)
    setHeaderRow1Height(el.getBoundingClientRect().height)
    return () => observer.disconnect()
  }, [columnsReady])

  // --- テーブルヘッダ全体の高さ計測（オーバーレイを本体だけに被せる＋行ウィンドウの起点に使う） ---
  useEffect(() => {
    const el = theadRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setTheadHeight(el.getBoundingClientRect().height))
    observer.observe(el)
    setTheadHeight(el.getBoundingClientRect().height)
    return () => observer.disconnect()
  }, [columnsReady])

  // --- loading 完了で 1 フレーム後にテーブルを表示（レイアウト計測のため） ---
  useEffect(() => {
    if (loading) {
      setColumnsReady(false)
      return
    }
    let cancelled = false
    const id = requestAnimationFrame(() => {
      if (!cancelled) setColumnsReady(true)
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(id)
    }
  }, [loading])

  // --- スクロール領域の幅・高さ計測 ---
  useEffect(() => {
    if (!columnsReady) return
    const el = tableScrollRef.current
    if (!el) return
    const measure = () => {
      setViewportWidth(el.clientWidth)
      setViewportHeight(el.clientHeight)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [columnsReady])

  // --- 1 行の実高さを計測（行ウィンドウ・spacer 高さの精度向上） ---
  useLayoutEffect(() => {
    if (!columnsReady) return
    const el = tableScrollRef.current
    if (!el) return
    const rowEl = el.querySelector('[data-matrix-row]')
    if (!rowEl) return
    const h = rowEl.getBoundingClientRect().height
    if (h > 0) setRowHeight((prev) => (Math.abs(prev - h) > 0.5 ? h : prev))
  }, [columnsReady, windowRows.length])

  // 縦横スクロール時、列・行ウィンドウ先頭を STEP 量子化して更新する（ピクセル単位では再レンダーしない）。
  const handleTableScroll = useCallback(() => {
    setOpenRowFilter(null)
    const el = tableScrollRef.current
    if (!el) return
    const rawCol = Math.floor((el.scrollLeft - LEFT_COLUMNS_TOTAL_WIDTH) / colWidth)
    const steppedCol = Math.max(0, Math.floor(Math.max(0, rawCol) / COLUMN_WINDOW_STEP) * COLUMN_WINDOW_STEP)
    setColWindowStart((prev) => (prev === steppedCol ? prev : steppedCol))

    const rawRow = Math.floor((el.scrollTop - theadHeight) / rowHeight)
    const steppedRow = Math.max(0, Math.floor(Math.max(0, rawRow) / ROW_WINDOW_STEP) * ROW_WINDOW_STEP)
    setRowWindowStart((prev) => (prev === steppedRow ? prev : steppedRow))
  }, [colWidth, rowHeight, theadHeight])

  /** 行フィルタヘッダボタン（大分類・中分類・ダッシュボード・グラフ） */
  const renderRowFilterButton = (key, label, leftClass) => (
    <th className={`p-1.5 sticky ${leftClass} z-20 align-top sticky-header-cell matrix-table-col-140`} rowSpan={2}>
      <div className="flex flex-col gap-1" data-row-filter={key}>
        <span className="font-semibold">{label}</span>
        <div
          className="action-area relative"
          ref={(el) => {
            rowFilterAnchorRefs.current[key] = el
          }}
        >
          <div
            className={`yearButton flex items-center justify-between rounded-xl pl-2 pr-1 w-full text-xs ${openRowFilter === key ? 'is-active' : ''}`}
            style={{ paddingTop: '2px', paddingBottom: '2px', minHeight: '24px' }}
            onClick={() => setOpenRowFilter((v) => (v === key ? null : key))}
          >
            <span className="yearButton__text truncate mr-1">
              {getRowFilterCountLabel(rowFilters[key], rowFilterOptions[key], prevOptionsRef.current?.[key])}
            </span>
            <span className="yearButton__icon flex items-center justify-center w-5 h-5 flex-shrink-0">
              <span className="icon-arrow" />
            </span>
          </div>
        </div>
      </div>
    </th>
  )

  return (
    <div className="table-wrap px-5 pb-5 pt-2 w-full max-w-full min-w-0 box-border overflow-hidden">
      {openRowFilter && rowFilterDropdownRect && (
        <RowFilterDropdown
          key={openRowFilter}
          openRowFilter={openRowFilter}
          dropdownRect={rowFilterDropdownRect}
          rowFilterOptions={rowFilterOptions}
          currentRowFilters={rowFilters}
          onFilterChange={onFilterChange}
          onClose={() => setOpenRowFilter(null)}
        />
      )}
      <div className="rounded-lg overflow-hidden min-w-0 relative">
        {/* 切替中のオーバーレイ。ヘッダ（thead）は隠さず本体（tbody）部分だけに被せる。 */}
        {overlayLoading && columnsReady && (
          <div
            className="absolute left-0 right-0 bottom-0 z-30 flex items-center justify-center bg-white/70"
            style={{ top: theadHeight }}
            aria-busy="true"
          >
            <span className="text-gray-600 text-sm animate-pulse">読み込み中...</span>
          </div>
        )}
        {columnsReady ? (
          <div
            ref={tableScrollRef}
            className="overflow-auto bg-white dashboard-item-matrix-scroll"
            onScroll={handleTableScroll}
          >
            <table
              className={`text-xs matrix-table ${fewColumns ? 'matrix-table--few-chrofy-columns' : ''}`}
              style={{ width: `${LEFT_COLUMNS_TOTAL_WIDTH + totalCols * colWidth}px` }}
            >
              <colgroup>
                <col style={{ width: COL_WIDTH_MAJOR }} />
                <col style={{ width: COL_WIDTH_MIDDLE }} />
                <col style={{ width: COL_WIDTH_DASHBOARD }} />
                <col style={{ width: COL_WIDTH_GRAPH }} />
                <col style={{ width: COL_WIDTH_REFERABLE }} />
                <col style={{ width: leftColSpacerWidth }} />
                {windowColumns.map((col) => (
                  <col key={col.column_code} style={{ width: colWidth }} />
                ))}
                <col style={{ width: rightColSpacerWidth }} />
              </colgroup>
              <thead ref={theadRef}>
                <tr ref={headerRow1Ref}>
                  {renderRowFilterButton('major', '大分類', 'left-0')}
                  {renderRowFilterButton('middle', '中分類', 'left-[140px]')}
                  {renderRowFilterButton('dashboard', 'ダッシュボード', 'left-[280px]')}
                  {renderRowFilterButton('graph', 'グラフ', 'left-[460px]')}
                  {/* 参照 - 単一選択 */}
                  <th
                    className="p-1 sticky left-[660px] z-20 align-top sticky-header-cell matrix-table-referable-cell w-[80px] min-w-[80px] max-w-[80px]"
                    rowSpan={2}
                  >
                    <div className="flex flex-col gap-1 min-w-0" data-row-filter="referable">
                      <span className="font-semibold">参照</span>
                      <div
                        className="action-area relative min-w-0"
                        ref={(el) => {
                          rowFilterAnchorRefs.current.referable = el
                        }}
                      >
                        <div
                          className={`yearButton flex items-center justify-between rounded-xl pl-2 pr-1 w-full min-w-0 text-[11px] ${openRowFilter === 'referable' ? 'is-active' : ''}`}
                          style={{ paddingTop: '2px', paddingBottom: '2px', minHeight: '24px', minWidth: 0 }}
                          onClick={() => setOpenRowFilter((v) => (v === 'referable' ? null : 'referable'))}
                        >
                          <span className="yearButton__text truncate mr-1">
                            {rowFilters.referable === 'all'
                              ? 'すべて'
                              : rowFilters.referable === 'referable'
                                ? '可'
                                : '不可'}
                          </span>
                          <span className="yearButton__icon flex items-center justify-center w-5 h-5 flex-shrink-0">
                            <span className="icon-arrow" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </th>
                  {/* 左列 spacer（ヘッダ2行ぶん rowSpan） */}
                  <th aria-hidden="true" rowSpan={2} style={{ padding: 0 }} />
                  {groupedWindowColumns.map((grp, grpIdx) => (
                    <th
                      key={grp.data_classification_code}
                      colSpan={grp.columns.length}
                      className="p-1 bg-[#d8dff0] text-center font-semibold matrix-col-group-header"
                      title={grp.data_classification_name}
                      style={{
                        top: 0,
                        ...(grpIdx < groupedWindowColumns.length - 1
                          ? { borderRight: `1px solid ${COLUMN_GROUP_BORDER_COLOR}` }
                          : {})
                      }}
                    >
                      {grp.data_classification_name}
                    </th>
                  ))}
                  {/* 右列 spacer */}
                  <th aria-hidden="true" rowSpan={2} style={{ padding: 0 }} />
                </tr>
                <tr>
                  {windowColumns.map((col, colIdx) => {
                    const showGroupBorder =
                      colIdx < windowColumns.length - 1 &&
                      windowColumns[colIdx + 1].data_classification_code !== col.data_classification_code
                    return (
                      <th
                        key={`${col.data_classification_code}-${col.column_code}`}
                        className="p-1 bg-[#E0E5F2] matrix-col-header"
                        style={{
                          height: 120,
                          top: headerRow1Height || 44,
                          verticalAlign: 'top',
                          ...(showGroupBorder ? { borderRight: `1px solid ${COLUMN_GROUP_BORDER_COLOR}` } : {})
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                          <div
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              minHeight: 0
                            }}
                          >
                            <button
                              type="button"
                              className="text-gray-900 hover:text-[#3150A5] cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-[1.2] max-h-[90px] overflow-hidden block"
                              title={col.column_name}
                              data-testid={`matrix-column-header-${col.column_code || ''}`}
                              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                              onClick={() => onColumnInfoClick(col)}
                            >
                              {col.column_name || col.column_code}
                            </button>
                          </div>
                          <div
                            style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', paddingBottom: '2px' }}
                          >
                            <CellDot required={true} status={statusByCode[col.column_code] || 'unset'} isHeader />
                          </div>
                        </div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={totalColSpan}
                      className="matrix-table-message-cell p-4 bg-white text-center text-gray-600"
                    >
                      読み込み中...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={totalColSpan}
                      className="matrix-table-message-cell p-4 bg-white text-center text-red-600"
                      role="alert"
                    >
                      {error}
                    </td>
                  </tr>
                ) : (
                  <>
                    {topSpacerHeight > 0 && (
                      <tr aria-hidden="true">
                        <td colSpan={totalColSpan} style={{ height: topSpacerHeight, padding: 0, border: 0 }} />
                      </tr>
                    )}
                    {windowRows.map((row, k) => (
                      <MatrixRow
                        key={`${row.graphId}-${rowStart + k}`}
                        row={row}
                        majorSpan={windowedSpans.major[k]}
                        middleSpan={windowedSpans.middle[k]}
                        dashboardSpan={windowedSpans.dashboard[k]}
                        columns={windowColumns}
                        statusByCode={statusByCode}
                        mappingLoading={mappingLoading}
                        onGraphClick={onGraphClick}
                        leftSpacerWidth={leftColSpacerWidth}
                        rightSpacerWidth={rightColSpacerWidth}
                      />
                    ))}
                    {bottomSpacerHeight > 0 && (
                      <tr aria-hidden="true">
                        <td colSpan={totalColSpan} style={{ height: bottomSpacerHeight, padding: 0, border: 0 }} />
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            className="overflow-auto bg-white dashboard-item-matrix-scroll flex items-center justify-center h-[calc(100vh-240px)] min-h-[800px] w-full max-w-[calc(100vw-200px)] min-w-0"
            aria-busy="true"
          >
            <span className="text-gray-500 animate-pulse">読み込み中...</span>
          </div>
        )}
      </div>
    </div>
  )
}

MatrixTable.propTypes = {
  filteredColumns: PropTypes.array.isRequired,
  leftColumnRowsFiltered: PropTypes.array.isRequired,
  statusByCode: PropTypes.object.isRequired,
  rowFilterOptions: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  mappingLoading: PropTypes.bool,
  overlayLoading: PropTypes.bool,
  onFilterChange: PropTypes.func.isRequired,
  onGraphClick: PropTypes.func.isRequired,
  onColumnInfoClick: PropTypes.func.isRequired
}

MatrixTable.defaultProps = {
  loading: false,
  error: null,
  mappingLoading: false,
  overlayLoading: false
}

export default MatrixTable
